import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

import Home from './pages/Home';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import Listing from './pages/Listing';
import InteriorDesignersArchitects from './pages/InteriorDesignersArchitects';
import ListingDetails from './pages/ListingDetails';
import MyAccountLayout from './components/MyAccountLayout';
import UserProfile from './pages/UserProfile';
import ManageListing from './pages/ManageListing';
import Inquiries from './pages/Inquiries';
import UpgradeMembership from './pages/UpgradeMembership';
import SupportTickets from './pages/SupportTickets';
import Reviews from './pages/Reviews';
import MyFavorites from './pages/MyFavorites';
import VendorDashboard from './pages/VendorDashboard';

import AdminDashboard from './pages/AdminDashboard';
import Unauthorized from './pages/Unauthorized';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import TermsConditions from './pages/TermsConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import { useAuth } from './hooks/useAuth';
import useSubdomainRedirect from './hooks/useSubdomainRedirect';

// Magazine Pages
import MagazinePage from './pages/magazine/MagazinePage';
import YourSubscriptionPage from './pages/magazine/YourSubscriptionPage';
import SubscribePage from './pages/magazine/SubscribePage';
import YearlyCheckoutPage from './pages/magazine/YearlyCheckoutPage';
import OrderCheckoutPage from './pages/magazine/OrderCheckoutPage';
import PaymentHistoryPage from './pages/magazine/PaymentHistoryPage';

const AppContent = () => {
  useSubdomainRedirect();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const path = location.pathname.toLowerCase();
  const isAuthPage = ['/login', '/register', '/sign-up', '/unauthorized'].includes(path);
  const isAdminPage = path.startsWith('/admin');
  const isMyAccountPage = path.startsWith('/my-account');

  const hideNavbar = isAuthPage || isAdminPage;
  const showFooter = !isAuthPage && !isAdminPage && !isMyAccountPage;

  return (
    <div className="flex flex-col min-h-screen">
      {!hideNavbar && <Navbar />}
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/about" element={<About />} />
          <Route path="/listing" element={<Listing />} />
          <Route path="/interior-designers-and-architects" element={<InteriorDesignersArchitects />} />
          <Route path="/interior-designers-architects" element={<Navigate to="/interior-designers-and-architects" replace />} />
          <Route path="/architects-designers" element={<Navigate to="/interior-designers-and-architects" replace />} />
          <Route path="/architects-and-designers" element={<Navigate to="/interior-designers-and-architects" replace />} />
          <Route path="/listing/:id" element={<ListingDetails />} />
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <Navigate to="/sign-up" replace />
            } 
          />
          <Route 
            path="/sign-up" 
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } 
          />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-cancellation-policy" element={<RefundPolicy />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Protected User Account Dashboard Routes */}
          <Route 
            path="/my-account" 
            element={
              <ProtectedRoute>
                <MyAccountLayout />
              </ProtectedRoute>
            } 
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<VendorDashboard />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="favorites" element={<MyFavorites />} />
            <Route path="manage-listing" element={<ManageListing />} />

            <Route path="inquiries" element={<Inquiries />} />
            <Route path="upgrade-membership" element={<UpgradeMembership />} />
            <Route path="support-tickets" element={<SupportTickets />} />
            <Route path="reviews" element={<Reviews />} />
            
            {/* Nested Magazine Routes */}
            <Route path="magazine" element={<MagazinePage />}>
              <Route index element={<Navigate to="subscribe" replace />} />
              <Route path="subscription" element={<YourSubscriptionPage />} />
              <Route path="subscribe" element={<SubscribePage />} />
              <Route path="yearly-checkout" element={<YearlyCheckoutPage />} />
              <Route path="order-checkout" element={<OrderCheckoutPage />} />
              <Route path="history" element={<PaymentHistoryPage />} />
            </Route>
          </Route>

          {/* Protected Admin Console Routes */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {showFooter && <Footer />}
    </div>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;
